import type { ChatToolCall } from '../api/domains/chatbot.api'

/**
 * Assistant tools that change Library items on the server. When a finished turn
 * carries a successful one, the Library pages refetch: `apps/console` has no
 * query cache to invalidate, so this module-level signal stands in for it.
 */
export const LIBRARY_WRITE_TOOLS: ReadonlySet<string> = new Set([
  'update_library_item',
  'share_library_item',
  'delete_library_items',
  'import_server_layers',
  'publish_file',
])

export interface LibraryChange {
  changedIds: string[]
  deletedIds: string[]
}

type Listener = (change: LibraryChange) => void

const listeners = new Set<Listener>()

export function subscribeLibraryRefresh(listener: Listener): () => void {
  listeners.add(listener)

  return () => {
    listeners.delete(listener)
  }
}

interface WriteResult {
  success?: unknown
  changed?: unknown
  item?: { id?: unknown }
  deleted?: unknown
  imported?: unknown
  published?: { id?: unknown }
}

export function announceLibraryWrites(toolCalls: readonly ChatToolCall[]): void {
  const changedIds: string[] = []
  const deletedIds: string[] = []

  for (const call of toolCalls) {
    if (!LIBRARY_WRITE_TOOLS.has(call.name)) continue
    const result = call.result as WriteResult | null

    if (!result || typeof result !== 'object' || result.success !== true) continue

    if (call.name === 'delete_library_items') {
      if (!Array.isArray(result.deleted)) continue

      for (const entry of result.deleted as Array<{ id?: unknown } | null>) {
        if (typeof entry?.id === 'string') deletedIds.push(entry.id)
      }

      continue
    }

    if (call.name === 'import_server_layers') {
      if (!Array.isArray(result.imported)) continue

      for (const entry of result.imported as Array<{ id?: unknown } | null>) {
        if (typeof entry?.id === 'string') changedIds.push(entry.id)
      }

      continue
    }

    // A publish touches the file and, once the job is done, the new layer or table.
    if (call.name === 'publish_file') {
      if (typeof result.item?.id === 'string') changedIds.push(result.item.id)
      if (typeof result.published?.id === 'string') changedIds.push(result.published.id)

      continue
    }

    // A share that changed nothing wrote nothing.
    if (call.name === 'share_library_item' && result.changed === false) continue
    if (typeof result.item?.id === 'string') changedIds.push(result.item.id)
  }

  announceLibraryChange({ changedIds, deletedIds })
}

/**
 * Tells every open Library page that items changed. For a write the browser made itself rather than
 * through an assistant tool — the assistant's attachment picker uploading a file is one.
 */
export function announceLibraryChange(change: LibraryChange): void {
  if (change.changedIds.length === 0 && change.deletedIds.length === 0) return

  for (const listener of [...listeners]) {
    try {
      listener(change)
    } catch {
      // One page's failed refresh must not stop the others.
    }
  }
}
